// keep a single React import below with hooks
import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Map from "../../Map";
import CesiumMap from "../../CesiumMap";
import PanoramaComponent from "components/Panorama";
import ProjectMainContent from "./ProjectMainContent";
import WithMapContent from "./WithMapContent";
import Invoice from "../../Account/Invoice";
import { setMapViewAction, setPanoViewAction } from "../../../redux/slices/mapbox/mapSlice";
import { createProjectMarkerAction, getProjectListAction } from "../../../redux/slices/project/list";
import { hideAllLayersAction } from "../../../redux/slices/layers";
import { removeSelectedProjectAction } from "../../../redux/slices/project/selected";

const MainContent = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const history = React.useMemo(() => ({
    push: (to) => navigate(to),
    replace: (to) => navigate(to, { replace: true }),
    goBack: () => navigate(-1)
  }), [navigate]);

  const mapView = useSelector((state) => state.mapState.mapView);
  const isCesiumView = useSelector((state) => state.mapState.is3D); 
  const projectMapView = useSelector((state) => state.projectList.mapView);
  const isPano = useSelector((state) => state.mapState.isPano);
  const selectedProjectId = useSelector((state) => state.selectedProject.id);
  const lastMapViewRef = useRef(mapView);
  const prevMapViewRef = useRef(mapView);
  const prevIsPanoRef = useRef(isPano);

  
  useEffect(() => {
    if (location.pathname === "/project") {
      dispatch(getProjectListAction());
      dispatch(removeSelectedProjectAction());
      dispatch(setMapViewAction({ mapView: projectMapView }));
    }
  }, [location.pathname, projectMapView, dispatch]);

  useEffect(() => {
    if (location.pathname === "/invoice") {
      dispatch(setMapViewAction({ mapView: false }));
    }
  }, [location.pathname, dispatch]);

  useEffect(() => {
    if (projectMapView) {
      // Ensure map view is enabled before creating markers
      if (!mapView && !isPano) {
        dispatch(setMapViewAction({ mapView: true }));
      }
      
      // Wait a bit for map to be visible, then create markers
      const timer = setTimeout(() => {
        if (window.map && window.map._markers) {
          window.map._markers.forEach((marker) => {
            try {
              marker.remove();
            } catch (error) {
              // Ignore removal errors
            }
          });
          window.map._markers = [];
        }
        if (!isPano) {
          dispatch(createProjectMarkerAction({ history }));
          dispatch(hideAllLayersAction());
        }
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [projectMapView, mapView, isPano, dispatch, history]);

  const renderComponent = () => {
    if (location.pathname === "/project") {
      return !mapView && <ProjectMainContent />;
    }
    if (location.pathname === "/invoice") {
      return !mapView && <Invoice />;
    }

    return null;
  };

  // Ensure map view is enabled on all non-exception routes (e.g., /flight)
  useEffect(() => {
    if (location.pathname !== "/project" && location.pathname !== "/invoice") {
      if (!mapView && !isPano) {
        dispatch(setMapViewAction({ mapView: true }));
      }
    }
  }, [location.pathname, mapView, isPano, dispatch]);

  // Track/restore previous mapView only when pano opens/closes to avoid getting stuck on white screens
  useEffect(() => {
    const wasPano = prevIsPanoRef.current;
    const prevMapView = prevMapViewRef.current;
    // Store mapView when entering pano
    if (!wasPano && isPano) {
      // Use the map visibility from the previous render so we don't store the already-updated false value
      lastMapViewRef.current = prevMapView;
    }
    // Restore only when exiting pano
    if (wasPano && !isPano) {
      const restoreMapView = lastMapViewRef.current ?? true;
      dispatch(setMapViewAction({ mapView: restoreMapView }));
      if (typeof window !== "undefined" && window.map?.resize) {
        setTimeout(() => {
          try { window.map.resize(); } catch (_) {}
        }, 50);
      }
    }
    prevIsPanoRef.current = isPano;
    prevMapViewRef.current = mapView;
  }, [isPano, mapView, dispatch]);

  // When map view becomes visible again (e.g., closing pano), force mapbox to resize
  useEffect(() => {
    if (mapView && !isPano && typeof window !== "undefined" && window.map?.resize) {
      const timer = setTimeout(() => {
        try { window.map.resize(); } catch (_) {}
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [mapView, isPano]);

  return (
    <>
      <Box
        style={{ width: "100%" }}
        sx={{
          display: mapView ? "block" : "none",
        }}
      >
        {!isCesiumView ? <Map key={selectedProjectId || 'map'} selectedProjectId={selectedProjectId} /> : <CesiumMap key={selectedProjectId || 'cesium'} />} 
      </Box>
      {!mapView && isPano ? (
        <PanoramaComponent /> 
      ) : (
        !mapView && renderComponent()
      )}
      {mapView && <WithMapContent />}
    </>
  );
};

export default MainContent;
